import { useState } from 'react';
import axios from 'axios';
import { formatDependencies } from '../utils/aircraftLifecycleUtils';

/**
 * Custom hook for managing assembly operations
 * Handles CRUD operations, approval workflow, and form state
 */
export const useAssemblyHandlers = (selectedAircraft, dropdownOptions, fetchAssemblies) => {
  const [open, setOpen] = useState(false);
  const [selectedAssembly, setSelectedAssembly] = useState(null);
  const [formData, setFormData] = useState({});

  const handleAddAssembly = () => {
    setSelectedAssembly(null);
    setFormData({
      aircraftId: selectedAircraft?._id || '',
      ataChapterId: '',
      type: '',
      manufacturerId: '',
      manufacturerNameId: '',
      modelId: '',
      serialNumber: '',
      position: '',
      installedOnDate: '',
      licenseId: '',
      partId: '',
      partNumber: '',
      partName: '',
      status: 'DRAFT',
      valuesAtInstallation: [],
      customColumns: [],
      customValuesAtInstallation: [],
      timeSinceRecords: [],
      asReceivedEvidenceFrom: '',
      isInitialLoad: false
    });
    setOpen(true);
  };

  const handleEditAssembly = async (assembly) => {
    setSelectedAssembly(assembly);
    // Find manufacturerNameId from manufacturer
    let manufacturerNameId = '';
    if (assembly.manufacturerId) {
      if (typeof assembly.manufacturerId === 'object') {
        // Populated manufacturer object - check for manufacturerName reference
        if (assembly.manufacturerId.manufacturerName) {
          // New structure: manufacturerName is populated
          manufacturerNameId = typeof assembly.manufacturerId.manufacturerName === 'object'
            ? assembly.manufacturerId.manufacturerName._id
            : assembly.manufacturerId.manufacturerName;
          // Note: Legacy name field has been removed - manufacturerName is always populated
        }
      } else {
        // Just an ID, fetch manufacturer from API
        try {
          const mfrResponse = await axios.get(`/api/master-data/manufacturers/${assembly.manufacturerId}`);
          if (mfrResponse.data?.manufacturerName) {
            manufacturerNameId = typeof mfrResponse.data.manufacturerName === 'object'
              ? mfrResponse.data.manufacturerName._id
              : mfrResponse.data.manufacturerName;
            // Note: Legacy name field has been removed - manufacturerName is always populated
          }
        } catch (error) {
          console.error('Error fetching manufacturer:', error);
        }
      }
    }
    
    setFormData({
      aircraftId: assembly.aircraftId?._id || assembly.aircraftId || '',
      ataChapterId: assembly.ataChapterId?._id || assembly.ataChapterId || '',
      type: assembly.type?._id || assembly.type || '',
      typeName: assembly.type?.name || assembly.typeName || '',
      manufacturerId: assembly.manufacturerId?._id || assembly.manufacturerId || '',
      manufacturerNameId: manufacturerNameId,
      modelId: assembly.modelId?._id || assembly.modelId || '',
      serialNumber: assembly.serialNumber || '',
      position: assembly.position || '',
      installedOnDate: assembly.installedOnDate ? new Date(assembly.installedOnDate).toISOString().split('T')[0] : '',
      licenseId: assembly.licenseId?._id || assembly.licenseId || '',
      partId: assembly.partId?._id || assembly.partId || '',
      partNumber: (typeof assembly.partId === 'object' && assembly.partId?.partNumber)
        ? String(assembly.partId.partNumber)
        : '',
      partName: (typeof assembly.partId === 'object' && assembly.partId?.partName)
        ? String(assembly.partId.partName).trim()
        : '',
      workOrderNumber: assembly.workOrderNumber || '',
      notes: assembly.notes || '',
      asReceivedEvidenceFrom: assembly.asReceivedEvidenceFrom || '',
      isInitialLoad: !!assembly.isInitialLoad,
      valuesAtInstallation: assembly.valuesAtInstallation || [],
      customColumns: assembly.customColumns || [],
      customValuesAtInstallation: assembly.customValuesAtInstallation || [],
      timeSinceRecords: assembly.timeSinceRecords || []
    });
    setOpen(true);
  };

  const handleSubmitAssembly = async () => {
    try {
      // Map manufacturerNameId to manufacturerId if needed
      // Note: Assembly model still uses Manufacturer (not ManufacturerName) for backward compatibility
      // We submit manufacturerNameId and let backend handle the mapping, or fetch Manufacturer ID
      const submitData = { ...formData };
      delete submitData.partNumber;
      delete submitData.partName;

      // Validate required fields before submission
      if (!submitData.aircraftId || !submitData.type || !submitData.serialNumber || 
          !submitData.position || !submitData.installedOnDate) {
        alert('Please fill in all required fields');
        return;
      }
      
      // Always map manufacturerNameId to manufacturerId if manufacturerNameId is set
      // This ensures we have the correct manufacturerId even when editing and manufacturer is changed
      if (submitData.manufacturerNameId) {
        // Find the selected ManufacturerName (handle both string and ObjectId comparisons)
        const manufacturerNameIdStr = String(submitData.manufacturerNameId);
        
        // Check if dropdown options are loaded
        if (!dropdownOptions.manufacturerNames || dropdownOptions.manufacturerNames.length === 0) {
          alert('Manufacturer dropdown options not loaded. Please refresh the page and try again.');
          return;
        }
        
        const selectedManufacturerName = dropdownOptions.manufacturerNames.find(
          mn => String(mn._id) === manufacturerNameIdStr
        );
        
        if (!selectedManufacturerName) {
          alert(`Manufacturer selection not found. The selected manufacturer may have been removed or the page needs to be refreshed.\n\nPlease:\n1. Refresh the page\n2. Select the manufacturer again\n3. Try submitting again`);
          return;
        }
        
        // Map selected Manufacturer Name to Manufacturer record from Master Data only (ID-only, single source of truth)
        try {
          const manufacturersResponse = await axios.get('/api/master-data/manufacturers', {
            params: { isActive: 'true' }
          });
          const data = manufacturersResponse.data;
          const isArr = Array.isArray(data);
          let matchingManufacturer = isArr ? data.find(m => {
            const mfrNameId = m.manufacturerName?._id?.toString() || m.manufacturerName?.toString();
            return mfrNameId === manufacturerNameIdStr;
          }) : undefined;
          
          if (!matchingManufacturer) {
            const allManufacturersResponse = await axios.get('/api/master-data/manufacturers');
            matchingManufacturer = allManufacturersResponse.data?.find(m => {
              const mfrNameId = m.manufacturerName?._id?.toString() || m.manufacturerName?.toString();
              return mfrNameId === manufacturerNameIdStr;
            });
            if (matchingManufacturer && !matchingManufacturer.isActive) {
              alert(`Manufacturer "${selectedManufacturerName.name}" exists but is inactive. Please activate it in Master Data Management or select a different manufacturer.`);
              return;
            }
          }
          
          if (matchingManufacturer) {
            submitData.manufacturerId = matchingManufacturer._id;
          } else {
            alert(`Manufacturer record not found for "${selectedManufacturerName.name}".\n\n` +
              `Manufacturer Name is registered in Master Data first. Create the Manufacturer record there:\n` +
              `1. Go to: Master Data Management > Manufacturer Setup\n` +
              `2. Add a new Manufacturer\n` +
              `3. Select Manufacturer Name: "${selectedManufacturerName.name}" (from dropdown)\n` +
              `4. Set Category and save\n\n` +
              `Then try adding the assembly again.`);
            return;
          }
        } catch (error) {
          console.error('Error fetching manufacturer for mapping:', error);
          alert(`Error mapping manufacturer: ${error.response?.data?.message || error.message}. Please try again.`);
          return;
        }
      }
      
      if (!submitData.manufacturerId) {
        alert('Please select a manufacturer.');
        return;
      }
      
      const objectIdPattern = /^[0-9a-fA-F]{24}$/;
      if (!objectIdPattern.test(submitData.manufacturerId)) {
        alert('Invalid manufacturer ID format. Please select a manufacturer from the dropdown.');
        return;
      }
      
      // Validate other required MongoId fields
      if (!submitData.ataChapterId || !submitData.modelId || !submitData.licenseId) {
        alert('Please fill in all required fields (ATA Chapter, Model Variant, and License).');
        return;
      }
      
      // Validate other ObjectId fields are valid
      const requiredObjectIds = [
        { field: 'ataChapterId', name: 'ATA Chapter' },
        { field: 'modelId', name: 'Model Variant' },
        { field: 'licenseId', name: 'License' }
      ];
      
      for (const { field, name } of requiredObjectIds) {
        if (submitData[field] && !objectIdPattern.test(submitData[field])) {
          alert(`Invalid ${name} ID format. Please select a valid ${name} from the dropdown.`);
          console.error(`Invalid ${field}:`, submitData[field]);
          return;
        }
      }
      
      // Clean valuesAtInstallation - remove entries with empty required fields
      if (submitData.valuesAtInstallation && Array.isArray(submitData.valuesAtInstallation)) {
        submitData.valuesAtInstallation = submitData.valuesAtInstallation.filter(val => {
          return val && 
                 val.columnName && val.columnName.trim() !== '' &&
                 val.assemblyValue && val.assemblyValue.trim() !== '' &&
                 val.airframeValue && val.airframeValue.trim() !== '';
        });
      } else {
        submitData.valuesAtInstallation = [];
      }

      // Ensure custom config arrays are sent (backend clears when assembly type has master config)
      if (!Array.isArray(submitData.customColumns)) submitData.customColumns = [];
      if (!Array.isArray(submitData.customValuesAtInstallation)) submitData.customValuesAtInstallation = [];
      
      // timeSinceRecords are already cleaned and prepared in AssemblyFormDialog via prepareTimeSinceRecordsForSubmission
      if (!Array.isArray(submitData.timeSinceRecords)) {
        submitData.timeSinceRecords = [];
      }
      
      // Remove empty string values for optional fields to avoid validation issues
      // Keep only fields that have actual values (required fields are already validated above)
      const cleanedSubmitData = {};
      Object.keys(submitData).forEach(key => {
        const value = submitData[key];
        // Keep arrays, objects, and non-empty strings
        // For optional string fields (notes, workOrderNumber), keep even if empty
        if (Array.isArray(value) || 
            (typeof value === 'object' && value !== null) ||
            (typeof value === 'string' && value !== '') ||
            (key === 'notes' || key === 'workOrderNumber') ||
            typeof value !== 'string') {
          cleanedSubmitData[key] = value;
        }
      });
      
      // Backend expects manufacturerId only (from Master Data)
      delete cleanedSubmitData.manufacturerNameId;
      
      if (selectedAssembly) {
        await axios.put(`/api/assemblies/${selectedAssembly._id}`, cleanedSubmitData);
      } else {
        await axios.post('/api/assemblies', cleanedSubmitData);
      }
      setOpen(false);
      fetchAssemblies();
      setSelectedAssembly(null);
    } catch (error) {
      console.error('Error saving assembly:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg ||
                          error.message || 
                          'Error saving assembly';
      alert(errorMessage);
    }
  };

  const handleSubmitForApproval = async (assemblyId) => {
    try {
      await axios.post(`/api/assemblies/${assemblyId}/submit`);
      fetchAssemblies();
    } catch (error) {
      alert(error.response?.data?.message || 'Error submitting for approval');
    }
  };

  const handleApprove = async (assemblyId) => {
    try {
      await axios.post(`/api/assemblies/${assemblyId}/approve`);
      fetchAssemblies();
    } catch (error) {
      alert(error.response?.data?.message || 'Error approving assembly');
    }
  };

  const handleReject = async (assemblyId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await axios.post(`/api/assemblies/${assemblyId}/reject`, { remarks: reason });
      fetchAssemblies();
    } catch (error) {
      alert(error.response?.data?.message || 'Error rejecting assembly');
    }
  };

  const handleDeleteAssembly = async (assembly) => {
    const isArchived = assembly.isActive === false;
    const action = isArchived ? 'permanently delete' : 'archive';
    let reason = '';
    
    if (isArchived) {
      if (!window.confirm(`⚠️ WARNING: This will PERMANENTLY DELETE the archived assembly.\n\nThis action cannot be undone!\n\nAre you sure you want to proceed?`)) {
        return;
      }
      reason = 'Permanently deleted';
    } else {
      // Check for active dependencies first (for active assemblies)
      try {
        const componentsResponse = await axios.get('/api/components', {
          params: { 
            aircraftId: selectedAircraft._id,
            assemblyId: assembly._id,
            isActive: 'true'
          }
        });
        
        if (componentsResponse.data && componentsResponse.data.length > 0) {
          alert(`Cannot archive assembly. It has ${componentsResponse.data.length} active component(s) associated with it. Please archive or remove all components first.`);
          return;
        }
      } catch (error) {
        console.error('Error checking dependencies:', error);
      }

      reason = window.prompt('Enter archive reason (optional):');
      if (reason === null) return; // User cancelled
      reason = reason || 'Archived by user';
      
      if (!window.confirm(`Are you sure you want to archive this assembly?`)) {
        return;
      }
    }

    try {
      await axios.delete(`/api/assemblies/${assembly._id}`, {
        data: { reason }
      });
      fetchAssemblies();
      alert(isArchived ? 'Assembly permanently deleted successfully' : 'Assembly archived successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.message || `Error ${action}ing assembly`;
      if (error.response?.data?.hasDependencies) {
        const depMessage = formatDependencies(error.response.data.dependencies);
        alert(errorMessage + depMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  return {
    open,
    selectedAssembly,
    formData,
    setFormData,
    setOpen,
    handleAddAssembly,
    handleEditAssembly,
    handleSubmitAssembly,
    handleSubmitForApproval,
    handleApprove,
    handleReject,
    handleDeleteAssembly
  };
};
